import React from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  REPORT_COUNT_LABELS,
  formatCount,
  reportBlockers,
  sortIssues,
} from "./researchLibraryFormat";

const CountGrid = ({ entries }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {entries.map(([key, label, value]) => (
      <div key={key} className="rounded-md border bg-muted/30 px-3 py-2">
        <div className="text-xl font-semibold tabular-nums">{formatCount(value)}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    ))}
  </div>
);

/** A validation report: counts, held reasons, blocking problems, and issues. */
export const ImportReport = ({ report }) => {
  if (!report) return null;
  const blockers = reportBlockers(report);
  const issues = sortIssues(report.issues, report.issue_descriptions);
  const held = Object.entries(report.held_reasons || {}).sort((a, b) => b[1] - a[1]);
  const counts = REPORT_COUNT_LABELS.map(([key, label]) => [
    key,
    label,
    report.counts?.[key],
  ]);

  return (
    <div className="space-y-4">
      <div className="text-sm">
        Library version{" "}
        <span className="font-semibold">{report.library_version || "—"}</span>
        {report.title_collisions != null && (
          <span className="text-muted-foreground">
            {" "}
            · {formatCount(report.title_collisions)} title collisions
          </span>
        )}
      </div>

      <CountGrid entries={counts} />

      {blockers.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> {blockers.length} blocking problem
            {blockers.length === 1 ? "" : "s"} — this release cannot be committed.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {blockers.map((b, i) => (
              <li key={i}>
                <span className="font-medium">{b.kind}:</span> {b.message}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> No integrity or contract problems.
        </div>
      )}

      {held.length > 0 && (
        <div>
          <p className="mb-1 text-sm font-medium">Held reasons</p>
          <ul className="space-y-1 text-sm">
            {held.map(([reason, count]) => (
              <li key={reason} className="flex justify-between gap-3">
                <span>{reason}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatCount(count)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {issues.length > 0 && (
        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Issues ({issues.length})
          </p>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr key={issue.code} className="border-t align-top">
                    <td className="px-3 py-1.5 font-mono">{issue.code}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatCount(issue.count)}
                    </td>
                    <td className="px-3 py-1.5">{issue.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/** What a commit wrote. */
export const CommitSummary = ({ summary }) => {
  if (!summary) return null;
  const entries = Object.entries(summary).map(([key, value]) => [
    key,
    key.replace(/_/g, " "),
    value,
  ]);
  const numeric = entries.filter(([, , v]) => typeof v === "number");
  const other = entries.filter(([, , v]) => typeof v !== "number");
  return (
    <div className="space-y-3">
      <CountGrid entries={numeric} />
      {other.map(([key, label, value]) => (
        <div key={key} className="text-sm">
          <span className="font-medium capitalize">{label}:</span>{" "}
          <span className="text-muted-foreground">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ImportReport;
